import { cn } from "@/lib/utils";
import {
  type AssignmentStartInfo,
  isAssignmentBarSuppressedReason,
} from "@/services/assignment";
import type { LockedSegmentReason } from "@/services/assignment/classifyInbound";

type AssignmentGroupBarProps = {
  group: AssignmentStartInfo & { lockedReason?: LockedSegmentReason };
};

/**
 * 기간배정(파견·휴직·부서이동 등) 구간 바.
 * 시작 셀 1곳에서만 렌더. width: calc(N * 100% - 4px) 로 N칸 오른쪽으로 늘어남.
 * z-[8]: 위반 바(z-10)보다 아래에 깔림.
 *
 * lockedReason:
 *   - "outbound": 우리 소속 간호사가 타 병동으로 나간 기간 → 기존 라벨(파견 등)
 *   - "outsider": 타 병동에서 온 간호사의 비파견 기간 → "타병동 근무" 라벨
 */
export default function AssignmentGroupBar({ group }: AssignmentGroupBarProps) {
  if (isAssignmentBarSuppressedReason(group.reason)) return null;

  const rangeLen = group.rangeLength ?? 1;
  const isOutsiderLocked = group.lockedReason === "outsider";
  const label = isOutsiderLocked
    ? "타병동 근무"
    : group.targetGroupName
      ? `${group.reason}(${group.targetGroupName})`
      : group.reason;

  return (
    <div
      className={cn(
        "assignment-group-bar",
        "absolute top-1/2 z-[8] pointer-events-none -translate-y-1/2 left-[2px]",
        "flex items-center justify-center overflow-hidden",
        "rounded-md text-xs font-medium animate-in fade-in duration-300",
        "border border-dashed border-foreground/40 bg-background text-muted-foreground",
        rangeLen === 1 ? "size-7.5" : "h-7.5"
      )}
      data-assignment-reason={isOutsiderLocked ? "outsider" : group.reason}
      style={{
        ...(rangeLen > 1 ? { width: `calc(${rangeLen} * 100% - 4px)` } : {}),
      }}
    >
      <span className="truncate px-1">{label}</span>
    </div>
  );
}
